import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminProducts } from "@/components/admin/AdminProducts";
import { AdminOrders } from "@/components/admin/AdminOrders";
import { AdminCJ } from "@/components/admin/AdminCJ";

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-[var(--line)] bg-[var(--bone)] p-6">
      <p className="eyebrow !text-[10px] text-[var(--ink-soft)]">{label}</p>
      <p className="font-display mt-3 text-4xl" data-testid={testid}>{value}</p>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  useEffect(() => {
    if (user?.role === "admin") loadStats();
  }, [user, loadStats]);

  if (user === null) return <div className="min-h-[60vh]" />;
  if (!user || user.role !== "admin") return <Navigate to="/account" replace />;

  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-32 pt-12 md:px-10" data-testid="admin-page">
      <p className="eyebrow text-[var(--terra-deep)]">Admin</p>
      <h1 className="font-display mt-3 text-5xl tracking-tight">Store dashboard</h1>
      <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Revenue (paid)" value={money(stats?.revenue)} testid="stat-revenue" />
        <Stat label="Paid orders" value={stats?.orders ?? 0} testid="stat-orders" />
        <Stat label="Products" value={stats?.products ?? 0} testid="stat-products" />
        <Stat label="Customers" value={stats?.customers ?? 0} testid="stat-customers" />
        <Stat label="Low stock (<10)" value={stats?.low_stock ?? 0} testid="stat-low-stock" />
      </div>
      <Tabs defaultValue="orders" className="mt-12">
        <TabsList className="rounded-full bg-[var(--sand)] p-1">
          <TabsTrigger value="orders" className="rounded-full px-6" data-testid="admin-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="products" className="rounded-full px-6" data-testid="admin-tab-products">Products</TabsTrigger>
          <TabsTrigger value="dropship" className="rounded-full px-6" data-testid="admin-tab-dropship">Dropship (CJ)</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-8"><AdminOrders onChange={loadStats} /></TabsContent>
        <TabsContent value="products" className="mt-8"><AdminProducts onChange={loadStats} /></TabsContent>
        <TabsContent value="dropship" className="mt-8"><AdminCJ onDone={loadStats} /></TabsContent>
      </Tabs>
    </div>
  );
}
